import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import usePersistentStateWithDefault from '../hooks/use-persistent-state-with-default';
import { RangeEnum } from '../model/range.enum';
import { lineChartColors } from '../theme/colors';
import Utils from '../utils/immersive_utils';

import usePersistentState from './use-persistent-state';
import { WebSocketContext } from './websocket-context/websocket-provider';
import { SUBSCRIPTIONS_KEY } from '../constants/websocket-events';
import { VolumeMultipleRequest } from './model/volumeData';
import { SnapshotRequest } from './model/snapshotData';
import { SnapshotTypeEnum } from '../model/snapshot_type';
import { GlobalContext } from './global-provider';
import { useSmartInactivityWake } from '../hooks/use-smart-inactivity-wake';

const KEY = 'TOKEN-VOLATILITY-SURFACE-TIMESERIES';

interface ITimeSeriesProvider {
  children: ReactNode;
  providerKey: string;
}

export enum VolatilityTypeEnum {
  REALIZED = 'Historical',
  IMPLIED = 'Implied',
}

export enum FrequencyTypeEnum {
  min1 = '1',
  min5 = '5',
  min10 = '10',
  min30 = '30',
  min15 = '15',
  hour1 = '60',
}

export enum VenueTypeEnum {
  DERIBIT = 'DERIBIT',
  BINANCE = 'BINANCE',
  MEXC = 'MEXC',
}

export enum VenueSourceEnum {
  DERIBIT = 'DERIBIT',
  BINANCE = 'BINANCE',
  MEXC = 'MEXC',
}

export const frequencyLabelMapping = {
  [FrequencyTypeEnum.min1]: '1 min',
  [FrequencyTypeEnum.min5]: '5 min',
  [FrequencyTypeEnum.min10]: '10 min',
  [FrequencyTypeEnum.min15]: '15 min',
  [FrequencyTypeEnum.min30]: '30 min',
  [FrequencyTypeEnum.hour1]: '1 hour',
};

interface ListTokenFrequencyVenue {
  name: string;
  tokens: {
    name: string;
    windows: {
      name: string;
      frequencies: string[];
    }[];
  }[];
}

export type ListTokenFrequency = ListTokenFrequencyVenue[];

export interface Series {
  id: string;
  color: string;
  label: string;
  value: string;
  currency: string;
  data: {
    time: number;
    value: number;
  }[];
  type: VolatilityTypeEnum;
  frequency: FrequencyTypeEnum;
  venue: VenueTypeEnum;
  error?: boolean;
}

interface Subscribs {
  type: VolatilityTypeEnum;
  currency: string;
  venue: VenueTypeEnum;
  frequency?: FrequencyTypeEnum;
}

interface ITokenVolatilityTimeseriesContext {
  addSeries: (
    token: string,
    windowOrTenor: string,
    type: VolatilityTypeEnum,
    frequency: FrequencyTypeEnum,
    venue: VenueTypeEnum,
  ) => void;
  deleteSeries: (serie: Series) => void;
  series: Series[];
  isLoading: boolean;
  range: RangeEnum;
  setRange: (range: RangeEnum) => void;
  initTimeStamp: string;
  updateInitTimeStamp: () => void;
  seriesExists: (
    token: string,
    value: string,
    type: VolatilityTypeEnum,
    frequency: FrequencyTypeEnum,
    venue: VenueTypeEnum,
  ) => boolean;
  listTokenFrequency: ListTokenFrequency;
  fetchListTokenFrequency: () => void;
  isRangeChanged: any;
  fetchingList: any;
  isListTokenFrequencyFetched: boolean;
}

export const TokenVolatilityTimeseriesContext = createContext<ITokenVolatilityTimeseriesContext>(
  {} as ITokenVolatilityTimeseriesContext,
);

export const TokenVolatilityTimeseriesProvider = (props: ITimeSeriesProvider) => {
  const { providerKey } = props;

  const STATE_PATH = [KEY, providerKey];

  const isFirstLoadRef = useRef(false);
  const isRangeChangedRef = useRef(false);
  const { marketDataOverviewCurrentTab: currentTab } = useContext(GlobalContext);

  const [series, setSeries] = usePersistentStateWithDefault<Series[]>(
    STATE_PATH,
    'series',
    [],
    'data',
    [],
  );
  const [range, setRange] = usePersistentState(STATE_PATH, 'range', RangeEnum.D1);
  const [subscribs, setSubscribs] = useState<Subscribs[]>([]);
  const fetchingListRef = useRef<Set<string>>(new Set());

  const isLoading = fetchingListRef.current.size > 0;

  const [initTimeStamp, setInitTimeStamp] = useState<string>(
    Utils.getInitDateByRange(new Date(), RangeEnum.D1).toISOString(),
  );

  const [listTokenFrequency, setListTokenFrequency] = useState<ListTokenFrequency>([]);
  const [isListTokenFrequencyFetched, setIsListTokenFrequencyFetched] = useState<boolean>(false);

  const { subscribeToChannel, isConnected, sendMessage, unsubscribeFromChannel } =
    useContext(WebSocketContext);

  const generateCompositeId = (id: string, currency?: string) => {
    return Utils.generateCompositeIdWithPrefix(id, currency || '', providerKey);
  };

  const getNextColor = (usedColors: string[]) => {
    const availableColors = lineChartColors.filter((color) => !usedColors.includes(color));
    return availableColors.length > 0 ? availableColors[0] : '#000000';
  };

  const addFetchToList = (id: string) => {
    fetchingListRef.current.add(id);
  };
  const removeFetchFromList = (id: string) => {
    fetchingListRef.current.delete(id);
  };

  const updateSeriesWithError = (seriesToUpdate: Series | Series[]) => {
    setSeries((prevSeries) => {
      let seriesArray = Array.isArray(seriesToUpdate) ? seriesToUpdate : [seriesToUpdate];

      seriesArray = seriesArray.map((s) =>
        s.data && s.data.length > 0 ? s : { ...s, error: true },
      );

      const filteredList = prevSeries.filter(
        (s) => !seriesArray.some((errSerie) => errSerie.id === s.id),
      );
      return [...filteredList, ...seriesArray];
    });
  };

  const sanitizeSeriesData = (data: { time: number; value: number }[]) => {
    return data
      .filter((value, index, self) => self.findIndex((v) => v.time === value.time) === index)
      .sort((a, b) => a.time - b.time);
  };

  const updateSeries = (seriesToUpdate: Series | Series[], isRangeChanged: boolean) => {
    setSeries((prevSeries) => {
      const seriesArray = Array.isArray(seriesToUpdate) ? seriesToUpdate : [seriesToUpdate];

      const updatedSeries = seriesArray.reduce((acc, serie) => {
        const existingSerie = acc.find((s) => s.id === serie.id);

        if (existingSerie) {
          let mergedData;
          if (isRangeChanged) {
            mergedData = sanitizeSeriesData(serie.data);
          } else {
            mergedData = sanitizeSeriesData([...existingSerie.data, ...serie.data]);
          }

          return acc.map((s) => (s.id === serie.id ? { ...s, data: mergedData } : s));
        }

        return [...acc, serie];
      }, prevSeries);

      return updatedSeries;
    });
  };

  const getSerieFromResult = (resultItem: any[], index: number) => {
    if (resultItem && resultItem.length > index && resultItem[index!]) {
      const time = new Date(resultItem[0] + 'Z').getTime() / 1000;

      const value = resultItem[index!];

      if (value === null || value === undefined || isNaN(value)) return null;

      return { time, value };
    }

    return null;
  };

  const handleResponse = (seriesArr: Series[], response: any, isRangeChanged: boolean) => {
    const { result, error, id } = response;

    if (error) {
      updateSeriesWithError(seriesArr);
      removeFetchFromList(id);
      return;
    }

    if (!result || !result.length) {
      updateSeriesWithError(seriesArr);
      removeFetchFromList(id);
      return;
    }

    const newItems = seriesArr.map((serie, index) => {
      return {
        ...serie,
        data: result
          .map((r: any) => getSerieFromResult(r, index + 1))
          .filter((d: any) => !!d?.value),
      };
    });

    removeFetchFromList(id);
    updateSeries(newItems, isRangeChanged);
  };

  const makeRequest = (
    requestType: VolatilityTypeEnum,
    id: string,
    begin: Date,
    end: Date,
    interval: string,
    series: Series[],
    currency?: string,
  ) => {
    switch (requestType) {
      case VolatilityTypeEnum.REALIZED:
        return {
          jsonrpc: '2.0',
          id: id,
          method: 'token_vol_time_series',
          params: {
            begin: begin.toISOString(),
            end: end.toISOString(),
            interval,
            queries: series.map((serie) => ({
              source: serie.venue,
              underlying: `${serie.currency}`,
              window: serie.value,
              frequency: serie.frequency,
            })),
          },
        };
      case VolatilityTypeEnum.IMPLIED: {
        const seriesVols = series.map((serie) => ({
          label: 'ATMF',
          tenor: serie.value,
        }));

        return new VolumeMultipleRequest(
          id,
          begin.toISOString(),
          end.toISOString(),
          interval,
          'DERIBIT',
          currency as string,
          seriesVols,
          'SVI',
        );
      }
      default:
        return null;
    }
  };

  const getSeriesData = useCallback(
    (
      requestType: VolatilityTypeEnum,
      beginDate: Date,
      endDate: Date,
      key: string,
      seriesArr: Series[],
      ra?: RangeEnum,
      currency?: string,
      isRangeChanged: boolean = false,
    ) => {
      return new Promise((resolve, reject) => {
        const r = ra ?? range;

        const id = generateCompositeId(key, currency);

        const req = makeRequest(
          requestType,
          id,
          beginDate,
          endDate,
          Utils.getIntervalByRange(r),
          seriesArr,
          currency,
        );

        sendMessage(JSON.stringify(req));

        addFetchToList(id);
        subscribeToChannel(id, (response: any) => {
          try {
            handleResponse(seriesArr, response, isRangeChanged);
            resolve('success');
          } catch (error) {
            removeFetchFromList(id);
            reject(error);
          }
        });
      });
    },
    [initTimeStamp, sendMessage, range, series],
  );

  const groupSeriesBy = (series: any, key: keyof Series) => {
    return series.reduce(
      (acc: any, curr: any) => {
        const groupKey = curr[key];
        (acc[groupKey] = acc[groupKey] || []).push(curr);
        return acc;
      },
      {} as Record<string, Series[]>,
    );
  };

  const handleResponseSubscription = useCallback(
    (
      response: any,
      requestType: VolatilityTypeEnum,
      currency: string,
      frequency?: FrequencyTypeEnum,
    ) => {
      const data = response?.params?.data;
      const rawDate = new Date(data?.timestamp + 'Z');
      rawDate.setSeconds(0);
      rawDate.setMilliseconds(0);
      const time = rawDate.getTime() / 1000;

      const isTimestampAligned = (timestamp: number, frequency: FrequencyTypeEnum) => {
        const date = new Date(timestamp * 1000);
        const freqMinutes = Utils.getMinutesByFrequency(frequency);
        if (freqMinutes === 60) {
          return date.getMinutes() === 0;
        }
        return date.getMinutes() % freqMinutes === 0;
      };

      if (requestType === VolatilityTypeEnum.IMPLIED) {
        const tenors = data?.tenors;

        setSeries((prev) => {
          const updatedSeries = prev.map((serie) => {
            if (
              serie.type === VolatilityTypeEnum.IMPLIED &&
              serie.currency === currency &&
              tenors?.[serie?.value]
            ) {
              const value = tenors?.[serie?.value]?.ATMF;

              if (value !== undefined) {
                const newData = [...serie.data, { time, value }]
                  .filter(
                    (value, index, self) => self.findIndex((v) => v.time === value.time) === index,
                  )
                  .sort((a, b) => a.time - b.time);

                return { ...serie, data: newData };
              }
            }
            return serie;
          });

          return updatedSeries;
        });

        return;
      }

      const windows = data?.windows || {};

      if (!frequency) return;

      if (!isTimestampAligned(time, frequency)) return;

      setSeries((prev) => {
        const updatedSeries = prev.map((serie) => {
          const value = windows[serie.value]?.[parseInt(serie.frequency)];
          if (
            serie.type === VolatilityTypeEnum.REALIZED &&
            value &&
            serie.currency === currency &&
            serie.frequency === frequency
          ) {
            const newData = [...serie.data, { time, value }]
              .filter(
                (value, index, self) => self.findIndex((v) => v.time === value.time) === index,
              )
              .sort((a, b) => a.time - b.time);

            return { ...serie, data: newData };
          }
          return serie;
        });

        return updatedSeries;
      });
    },
    [setSeries],
  );

  const makeSubscription = useCallback(
    (
      currency: string,
      type: VolatilityTypeEnum,
      source: VenueTypeEnum,
      frequency?: FrequencyTypeEnum,
    ) => {
      const now = new Date();
      now.setSeconds(0);
      now.setMilliseconds(0);

      const key =
        type === VolatilityTypeEnum.REALIZED
          ? SUBSCRIPTIONS_KEY.TOKEN_VOLATILITY_TIMESERIES_SUBSCRIPTION
          : SUBSCRIPTIONS_KEY.TOKEN_VOLATILITY_TIMESERIES_SUBSCRIPTION_CANDLE;

      const id = generateCompositeId(key, currency);

      const interval = Utils.getIntervalByRange(range);
      const date = Utils.updateDateToInterval(now, interval);

      const historicalReq = {
        jsonrpc: '2.0',
        id: id,
        method: 'subscribe.token_vol_time_series',
        params: {
          timestamp: date.toISOString(),
          interval,
          source,
          underlying: `${currency}`,
          ...(frequency ? { frequency } : {}),
        },
      };

      const volsReq = new SnapshotRequest(id, currency, SnapshotTypeEnum.CM, now.toISOString());

      const req = type === VolatilityTypeEnum.IMPLIED ? volsReq : historicalReq;
      sendMessage(JSON.stringify(req));

      subscribeToChannel(id, (response: any) => {
        handleResponseSubscription(response, type, currency, frequency);
      });
    },
    [sendMessage, subscribeToChannel, series, range, handleResponseSubscription],
  );

  const validateSubscribs = (seriesArr: Series[]) => {
    const newSubscribs: Subscribs[] = [];
    const seriesByCurrency = groupSeriesBy(seriesArr, 'currency');

    Object.keys(seriesByCurrency).forEach((currency) => {
      const seriesByType = groupSeriesBy(seriesByCurrency[currency], 'type');
      Object.keys(seriesByType).forEach((type) => {
        if (type === VolatilityTypeEnum.REALIZED) {
          const seriesByVenue = groupSeriesBy(seriesByType[type], 'venue');
          Object.keys(seriesByVenue).forEach((venue) => {
            const seriesByFrequency = groupSeriesBy(seriesByVenue[venue], 'frequency');
            Object.keys(seriesByFrequency).forEach((frequency) => {
              newSubscribs.push({
                type: type as VolatilityTypeEnum,
                currency: currency as string,
                venue: venue as VenueTypeEnum,
                frequency: frequency as FrequencyTypeEnum,
              });
            });
          });
        } else {
          const seriesByVenue = groupSeriesBy(seriesByType[type], 'venue');
          Object.keys(seriesByVenue).forEach((venue) => {
            newSubscribs.push({
              type: type as VolatilityTypeEnum,
              currency: currency as string,
              venue: venue as VenueTypeEnum,
            });
          });
        }
      });
    });

    const newSubscriptions = newSubscribs.filter(
      (sub) =>
        !subscribs.some(
          (s) =>
            s.type === sub.type &&
            s.currency === sub.currency &&
            s.venue === sub.venue &&
            (sub.type === VolatilityTypeEnum.REALIZED ? s.frequency === sub.frequency : true),
        ),
    );

    const removedSubscriptions = subscribs.filter(
      (sub) =>
        !newSubscribs.some(
          (s) =>
            s.type === sub.type &&
            s.currency === sub.currency &&
            s.venue === sub.venue &&
            (sub.type === VolatilityTypeEnum.REALIZED ? s.frequency === sub.frequency : true),
        ),
    );

    newSubscriptions.forEach((sub) => {
      makeSubscription(
        sub.currency,
        sub.type,
        sub.venue,
        sub.type === VolatilityTypeEnum.REALIZED ? sub.frequency : undefined,
      );
    });

    removedSubscriptions.forEach((sub) => {
      const key =
        sub.type === VolatilityTypeEnum.REALIZED
          ? SUBSCRIPTIONS_KEY.TOKEN_VOLATILITY_TIMESERIES_SUBSCRIPTION
          : SUBSCRIPTIONS_KEY.TOKEN_VOLATILITY_TIMESERIES_SUBSCRIPTION_CANDLE;

      const id =
        sub.type === VolatilityTypeEnum.REALIZED
          ? generateCompositeId(key + '_' + sub.frequency, sub.currency)
          : generateCompositeId(key, sub.currency);

      unsubscribeFromChannel(id);
    });

    setSubscribs(newSubscribs);
  };

  const makeSerieId = (
    token: string,
    value: string,
    type: VolatilityTypeEnum,
    frequency: FrequencyTypeEnum,
    venue: VenueTypeEnum,
  ) => {
    let id = `${type}_${token}_${value}`;
    if (type === VolatilityTypeEnum.REALIZED) {
      id += `_${frequency}`;
    }
    id += `_${venue}`;
    return id;
  };

  const makeLabel = (
    value: string,
    type: VolatilityTypeEnum,
    frequency: FrequencyTypeEnum,
    venue: VenueTypeEnum,
    token?: string,
  ) => {
    const venueLabel = venue.charAt(0) + venue.slice(1).toLowerCase();
    const tokenLabel = token ? token.replace(/(_)?(USDC|USDT|USD)$/g, '').toUpperCase() : '';
    let typeLabel = '';
    if (type === VolatilityTypeEnum.REALIZED) {
      typeLabel = 'Real. Vol';
    } else {
      typeLabel = 'Impl. Vol';
    }
    const frequencyLabel = frequencyLabelMapping[frequency] || frequency;

    if (type === VolatilityTypeEnum.REALIZED) {
      return `${venueLabel} ${tokenLabel} ${typeLabel} ${value}/${frequencyLabel}`;
    } else {
      return `${tokenLabel} ${typeLabel} ${value}`;
    }
  };

  const getEarliestSeriesTimestamp = (): number | null => {
    if (!series || series.length === 0) return null;

    let earliestTimestamp: number | null = null;

    series.forEach((serie) => {
      if (serie.data && serie.data.length > 0) {
        const serieEarliestTime = Math.min(...serie.data.map((d) => d.time));

        if (earliestTimestamp === null || serieEarliestTime < earliestTimestamp) {
          earliestTimestamp = serieEarliestTime;
        }
      }
    });

    return earliestTimestamp;
  };

  const addSeries = (
    token: string,
    value: string,
    type: VolatilityTypeEnum,
    frequency: FrequencyTypeEnum,
    venue: VenueTypeEnum,
  ) => {
    const minTimestamp = getEarliestSeriesTimestamp();

    const endDate = new Date();

    let initDate = Utils.getInitDateByRange(endDate, range);
    if (minTimestamp !== null) {
      initDate = new Date(minTimestamp * 1000);
    }

    const usedColors = series.map((s) => s.color);
    const newColor = getNextColor(usedColors);

    const seriesArr = [
      {
        id: makeSerieId(token, value, type, frequency, venue),
        label: makeLabel(value, type, frequency, venue, token),
        color: newColor,
        currency: token,
        data: [],
        type,
        value,
        frequency,
        venue,
      },
    ];

    getSeriesData(
      type,
      initDate,
      new Date(),
      type === VolatilityTypeEnum.REALIZED ? KEY : KEY + '_IMPLIED',
      seriesArr,
      range,
      token,
      false,
    );
  };

  const seriesExists = (
    token: string,
    value: string,
    type: VolatilityTypeEnum,
    frequency: FrequencyTypeEnum,
    venue: VenueTypeEnum,
  ): boolean => {
    return series?.some((s) => s.id === makeSerieId(token, value, type, frequency, venue));
  };

  const deleteSeries = useCallback(
    (serie: Series) => {
      setSeries((prev) => {
        const nArr = prev.filter((s) => s.id !== serie.id);
        return nArr;
      });
    },
    [setSeries],
  );

  const resetSubscriptions = (seriesArr: Series[]) => {
    const seriesByCurrency = groupSeriesBy(seriesArr, 'currency');

    Object.keys(seriesByCurrency).forEach((currency) => {
      const seriesByType = groupSeriesBy(seriesByCurrency[currency], 'type');

      Object.keys(seriesByType).forEach((type) => {
        const seriesByVenue = groupSeriesBy(seriesByType[type], 'venue');
        Object.keys(seriesByVenue).forEach((venue) => {
          const key =
            type === VolatilityTypeEnum.REALIZED
              ? SUBSCRIPTIONS_KEY.TOKEN_VOLATILITY_TIMESERIES_SUBSCRIPTION
              : SUBSCRIPTIONS_KEY.TOKEN_VOLATILITY_TIMESERIES_SUBSCRIPTION_CANDLE;

          const id = generateCompositeId(key, currency as string);
          unsubscribeFromChannel(id);
          makeSubscription(currency as string, type as VolatilityTypeEnum, venue as VenueTypeEnum);
        });
      });
    });
  };

  const fetchListTokenFrequency = useCallback(() => {
    const key = 'LIST_TOKEN_VOL_TS_REQUEST';
    const id = generateCompositeId(key);

    const req = {
      jsonrpc: '2.0',
      id,
      method: 'list_token_vol_ts_request',
      params: {
        source: Object.values(VenueSourceEnum),
      },
    };

    sendMessage(JSON.stringify(req));
    addFetchToList(id);
    subscribeToChannel(id, (response: any) => {
      try {
        const { result, error } = response;

        if (error) {
          removeFetchFromList(id);
          return;
        }

        const sources = result?.sources ?? [];

        const groupedData: ListTokenFrequency = [];

        sources.forEach((srcObj: any) => {
          Object.entries(srcObj).forEach(([source, tokens]: [string, any]) => {
            if (Array.isArray(tokens)) {
              const sourceEntry = {
                name: source,
                tokens: tokens.map((token: any) => ({
                  name: token.underlying,
                  windows: Object.entries(token.windows || {}).map(([windowName, frequencies]) => ({
                    name: windowName,
                    frequencies: Array.isArray(frequencies) ? frequencies : [],
                  })),
                })),
              };
              groupedData.push(sourceEntry);
            }
          });
        });

        setListTokenFrequency(groupedData);
        setIsListTokenFrequencyFetched(true);
        removeFetchFromList(id);
      } catch {
        removeFetchFromList(id);
        setIsListTokenFrequencyFetched(true);
      }
    });
  }, [sendMessage, subscribeToChannel, providerKey]);

  const fetchHistoricalData = useCallback(
    (historicalSeries: Series[], initDate: Date, endDate: Date, rangeChanged: boolean) => {
      getSeriesData(
        VolatilityTypeEnum.REALIZED,
        initDate,
        endDate,
        KEY,
        historicalSeries,
        range,
        undefined,
        rangeChanged,
      );
    },
    [range],
  );

  const fetchImpliedData = (
    impliedSeries: Series[],
    initDate: Date,
    endDate: Date,
    range: RangeEnum,
    rangeChanged: boolean,
  ) => {
    const seriesByCurrency = groupSeriesBy(impliedSeries, 'currency');
    Object.keys(seriesByCurrency).forEach((currency) => {
      getSeriesData(
        VolatilityTypeEnum.IMPLIED,
        initDate,
        endDate,
        KEY + '_IMPLIED',
        seriesByCurrency[currency],
        range,
        currency as string,
        rangeChanged,
      );
    });
  };

  const getDates = (rangeChanged: boolean) => {
    let initDate = new Date();
    let endDate = new Date();

    const interval = Utils.getIntervalByRange(range);

    if (rangeChanged) {
      const newInitTimestamp = Utils.getInitDateByRange(new Date(), range).toISOString();

      initDate = new Date(newInitTimestamp);
      endDate = Utils.updateDateToInterval(new Date(), interval);
    } else {
      const earliestTimestamp = getEarliestSeriesTimestamp();

      let currentInitialDate = Utils.getInitDateByRange(new Date(), range);

      if (earliestTimestamp !== null) {
        currentInitialDate = new Date(earliestTimestamp * 1000);
      }

      initDate = Utils.getInitDateByRange(currentInitialDate, range);
      endDate = Utils.updateDateToInterval(currentInitialDate, interval);
    }

    return {
      initDate,
      endDate,
    };
  };

  const handleFetchSeries = useCallback(
    (rangeChanged = false) => {
      if (!isConnected || series.length === 0 || isLoading) return;

      const { initDate, endDate } = getDates(rangeChanged);

      const seriesWithoutErrors = series.filter((s) => !s.error);
      const validatedSeries = validateSeriesColors(seriesWithoutErrors);

      const seriesByType = groupSeriesBy(validatedSeries, 'type');
      const historicalSeries = seriesByType?.[VolatilityTypeEnum.REALIZED];
      const impliedSeries = seriesByType?.[VolatilityTypeEnum.IMPLIED];

      if (historicalSeries?.length > 0) {
        fetchHistoricalData(historicalSeries, initDate, endDate, rangeChanged);
      }

      if (impliedSeries?.length > 0) {
        fetchImpliedData(impliedSeries, initDate, endDate, range, rangeChanged);
      }
    },
    [isConnected, series, isLoading, range, fetchHistoricalData, setInitTimeStamp],
  );

  const updateInitTimeStamp = () => {
    if (isLoading) return;
    handleFetchSeries();
  };

  const validateRangeChange = () => {
    const rangeChanged = true;
    isRangeChangedRef.current = true;
    handleFetchSeries(rangeChanged);

    resetSubscriptions(series);
  };

  useEffect(() => {
    if (isConnected && series.length > 0) {
      const seriesWithoutErrors = series.filter((s) => !s.error);

      validateSubscribs(seriesWithoutErrors);
    }
  }, [series, isConnected]);

  useEffect(() => {
    validateRangeChange();
    isRangeChangedRef.current = true;
  }, [range]);

  useEffect(() => {
    if (!isFirstLoadRef.current && isConnected) {
      if (series && series.length > 0) {
        isFirstLoadRef.current = true;

        handleFetchSeries(true);
      }
    }
  }, [series, range, isConnected, currentTab, providerKey]);

  useEffect(() => {
    if (!isConnected) {
      fetchingListRef.current = new Set();
    }
  }, [isConnected]);

  useSmartInactivityWake(() => {
    if (isConnected) {
      handleFetchSeries(true);
    }
  });

  const validateSeriesColors = (series: Series[]): Series[] => {
    const usedColors: string[] = [];
    return series.map((s) => {
      if (!lineChartColors.includes(s.color)) {
        const newColor = getNextColor(usedColors);
        usedColors.push(newColor);
        return { ...s, color: newColor };
      }
      usedColors.push(s.color);
      return s;
    });
  };

  return (
    <TokenVolatilityTimeseriesContext.Provider
      value={{
        addSeries,
        deleteSeries,
        series: validateSeriesColors(series),
        isLoading,
        range,
        setRange,
        updateInitTimeStamp,
        seriesExists,
        initTimeStamp,
        listTokenFrequency,
        fetchListTokenFrequency,
        fetchingList: fetchingListRef.current.size,
        isRangeChanged: isRangeChangedRef,
        isListTokenFrequencyFetched,
      }}
    >
      {props.children}
    </TokenVolatilityTimeseriesContext.Provider>
  );
};
